import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Tooltip,
  Stack,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import { awsIcons } from "./AWSIcons";
import moment from "moment";
import { myLocalStorage } from "../../../components/StorageHelper";
import axios from "axios";
import { NGROK } from "../../../APIs";
import SyncIcon from "@mui/icons-material/Sync";
import useUserStore from "../../../services/userStore";
import useNotificationsStore from "../../../services/notificationStore";
import LoginToAWS from "./LoginToAws";
import { AiOutlineBarChart } from "react-icons/ai";
import { FaServer } from "react-icons/fa";
import useCloudSyncStore from "../../../services/useCloudSyncStore";
import useNotificationStore from "../../../services/notificationStore";
import TailwindFilterHeader from "../../../tailwind-ui/components/TailwindFilterHeader";
import UserRoleAnalysis from "../GCP/Components/UserRoleAnalysis";
import TailwindCollapsibleTable from "../../../tailwind-ui/components/TailwindCollapsibleTable";
import TimelineIcon from "@mui/icons-material/Timeline";
import CloudEntityAnalytics from "../../../components/v2components/CloudAnalytics/CloudEntityAnalytics";

const AWSUserDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { role, email } = useUserStore((state) => state.user);
  const { userAWSApprovedNotifications, adminawsNotifications } =
    useNotificationsStore();
  const [onAWSRequestResourceAccessForm, setOnAWSRequestResourceAccessForm] =
    useState(false);
  const [generateTemporaryPassword, setGenerateTemporaryPassword] =
    useState(false);
  const { user: initialUser } = location.state || { user: {} };
  const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
  const [selectedAccess, setSelectedAccess] = useState("resources");
  const [user, setUser] = useState(initialUser);
  const [groups, setGroups] = useState(initialUser?.groups || []);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncInitial, setSyncInitial] = useState(false);
  const [syncError, setSyncError] = useState(null);
  const [alertSeverity, setAlertSeverity] = useState("success");
  const { username, arn, createDate, userPolicies = [], userId } = user;
  const [selectedTab, setSelectedTab] = useState("analytics");

  const syncStatus = useCloudSyncStore((state) => state.syncStatus);
  let { awsConfig } = useNotificationStore.getState();
  const [awsAccountId, setAWSAccountId] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [accountIdToDataMap, setAccountIdToDataMap] = useState({});
  const [accountName, setAccountName] = useState("");
  const selectedAwsAccount = myLocalStorage.getItem("selectedAwsAccount");

  const handleAccountChange = async (value) => {
    const account = accounts.find((account) => account.value === value);
    if (account !== undefined) {
      setGenerateTemporaryPassword(false);
      myLocalStorage.setItem("selectedAwsAccount", account.value);
      setAWSAccountId(account.value);
      const accountData = accountIdToDataMap?.[account.value] || {};
      myLocalStorage.setItem("userDetails", JSON.stringify(accountData));
    }
  };

  const consolidatePolicies = () => {
    const userPolicyNames =
      userPolicies?.map((policy) => policy?.policyName) || [];
    const groupPolicyNames =
      groups?.flatMap((group) =>
        group?.groupPolicies?.map((policy) => policy?.policyName),
      ) || [];

    return [...userPolicyNames, ...groupPolicyNames];
  };
  const consolidatedPolicies = consolidatePolicies();

  const getDisplayItems = (items) => items.slice(0, 2);
  const getMoreCount = (items) => items.length - 2;

  const getAllDetails = (val) => {
    setSelectedAccess(val);
  };

  const getSpecificUsers = async (arn) => {
    const response = await axios.get(
      `${NGROK}/api/aws/getSpecificUser?policyarn=${arn}&tenantName=${latestTenant}&accountId=${selectedAwsAccount}`,
    );
    return response.data;
  };

  const onDemand = async (arn) => {
    try {
      const response = await axios.post(
        `${NGROK}/api/aws/ondemandSpecificUsers?tenantName=${latestTenant}&accountId=${selectedAwsAccount}`,
      );
    } catch (error) {
      setAlertSeverity("error");
      setSyncError(error.response.data.message);
    }
  };

  const fetchAWSUserConfiguration = async (isInital = false) => {
    const response = await axios.get(
      `${NGROK}/api/aws/getAWSUserConfig?tenantName=${latestTenant}&tenantEmail=${email}`,
    );
    if (response?.data) {
      const accounts = response.data.map((account) => ({
        label: account.awsAccountName,
        value: account.awsAccountId,
      }));

      setAccounts(accounts);

      const storedAccountId = myLocalStorage.getItem("selectedAwsAccount");
      const accountExists = accounts.find(
        (account) => account.value === storedAccountId,
      );

      if (
        typeof storedAccountId === "string" &&
        storedAccountId.trim() !== "" &&
        accountExists != null
      ) {
        setAWSAccountId(storedAccountId);
      } else {
        const defaultAccountId = accounts[0]?.value || "";
        setAWSAccountId(defaultAccountId);
        myLocalStorage.setItem("selectedAwsAccount", defaultAccountId);
      }

      const accountMap = {};
      response.data.forEach((account) => {
        accountMap[account.awsAccountId] = account;
      });

      setAccountIdToDataMap(accountMap);
    } else {
      console.error("No AWS user configuration found");
    }
  };

  const fetchAWSUserConfigurationForAccount = async () => {
    setIsSyncing(true);

    if (!selectedAwsAccount) {
      console.warn("No AWS account selected");
      setIsSyncing(false);
      return;
    }
    await onDemand();

    const accountData = accountIdToDataMap?.[selectedAwsAccount] || {};
    const awsAccountARN = accountData?.awsAccountARN ?? null;
    const awsUserName = accountData?.awsUserName ?? null;

    const isConfigured = !!awsAccountARN;

    if (isConfigured && awsUserName) {
      try {
        myLocalStorage.setItem("userDetails", JSON.stringify(accountData));
        await handleSync();
      } catch (error) {
        console.error("Error fetching AWS configuration details:", error);
      } finally {
        setIsSyncing(false);
      }
    } else {
      console.warn("Missing AWS ARN or Username for selected account");
    }
    setIsSyncing(false);
  };

  const raiseConsoleAccess = () => {
    setOnAWSRequestResourceAccessForm(true);
  };

  const consoleLevelAccess = userAWSApprovedNotifications?.filter(
    (userAWSNotification) =>
      userAWSNotification.accessType === "console" &&
      userAWSNotification.awsAccountId === selectedAwsAccount &&
      userAWSNotification.requestStatus === "APPROVED",
  );

  const checkAccessTiming = () => {
    const record = consoleLevelAccess;

    if (record.length === 0) return false;

    const schedules = record[0].schedules;

    if (!schedules || schedules.length === 0) {
      return record;
    }

    const nowUTC = moment.utc();

    const isActive = schedules.some((schedule) => {
      const scheduledStartTime = moment.utc(schedule.scheduledStartTime);
      const scheduledEndTime = moment.utc(schedule.scheduledEndTime);
      const isWithinSchedule = nowUTC.isBetween(
        scheduledStartTime,
        scheduledEndTime,
        null,
        "[]",
      );
      return isWithinSchedule;
    });

    if (isActive) return record;
    else return false;
  };

  const hasConsoleAccess = checkAccessTiming();
  const openAWSLogin = () => {
    console.log("Opening AWS Login");
    setGenerateTemporaryPassword(true);
  };

  const handleSync = async (isFirstSync = false) => {
    if (isFirstSync) {
      setSyncInitial(true);
    }
    const userDetails = myLocalStorage.getItem("userDetails");
    const userarn = JSON.parse(userDetails).awsAccountARN;
    const accountName = JSON.parse(userDetails).awsAccountName;
    setIsSyncing(true);
    await onDemand();
    const userResponse = await getSpecificUsers(userarn);
    setAccountName(accountName || "");
    setUser(userResponse);
    setGroups(userResponse?.groups !== null ? userResponse?.groups : []);

    setIsSyncing(false);
    setSyncInitial(false);
  };

  useEffect(() => {
    if (role === "TENANT_USER") {
      fetchAWSUserConfiguration();
    } else {
      console.log("Fetching AWS user configuration for admin");
      setAccountName(location.state?.selectedAwsAccount || "");
      const isconfigured = location.state?.isconfigured || false;
      if (isconfigured && !syncInitial) {
        setSyncInitial(true);
        handleSync();
      }
    }
  }, [location.state]);

  useEffect(() => {
    if (role === "TENANT_USER") {
      setSelectedTab("resources")
    }
  }, [role])

  useEffect(() => {
    if (role === "TENANT_USER") {
      fetchAWSUserConfigurationForAccount();
    }
  }, [accountIdToDataMap]);
  useEffect(() => {
    if (role === "TENANT_USER") {
      handleSync(true);
    }
  }, [selectedAwsAccount]);
  return <CloudEntityAnalytics cloudProvider={"aws"} email={username} entityId={arn} entityType="user" projectId={selectedAwsAccount} roles={consolidatedPolicies || []} syncedAt={createDate} entityName={selectedAwsAccount} />
 
};

export default AWSUserDetails;
